import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, RotateCcw, Undo2, XCircle, CheckCircle2, Clock, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const fmtDate = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };

const TABS = [
  { k: '', label: '전체' },
  { k: 'in_progress', label: '처리중' },
  { k: 'completed', label: '완료' },
  { k: 'rejected', label: '반려' },
  { k: 'withdrawn', label: '철회' },
];

const TYPE_META = {
  cancel:   { icon: XCircle,  label: '취소',  cls: 'text-red-700 bg-red-50 border-red-200' },
  exchange: { icon: RotateCcw, label: '교환', cls: 'text-orange-700 bg-orange-50 border-orange-200' },
  return:   { icon: Undo2,    label: '반품',  cls: 'text-purple-700 bg-purple-50 border-purple-200' },
};

const STATUS_LABEL = {
  requested: '신청', approved: '승인', pickup_in_progress: '회수중',
  received: '입고완료', reshipping: '재배송중', refund_processing: '환불처리중',
  completed: '완료', rejected: '반려', withdrawn: '철회',
};

export default function MyRma() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/my/rma', { params: tab ? { status: tab } : {} });
      setRows(r.data || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error('로그인이 필요합니다.'); navigate('/login'); return; }
      toast.error(e?.response?.data?.detail || '불러오기 실패');
    } finally { setLoading(false); }
  }, [tab, navigate]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (rmaId) => {
    try { const r = await api.get(`/my/rma/${rmaId}`); setDetail(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
  };
  const withdraw = async (rmaId) => {
    if (!window.confirm('요청을 철회하시겠습니까?')) return;
    try {
      await api.post(`/my/rma/${rmaId}/withdraw`, {});
      toast.success('요청을 철회했습니다.');
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '철회 실패'); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16" data-testid="my-rma-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white mb-4">
          <ArrowLeft size={14} /> 마이페이지
        </Link>
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>취소·교환·반품 내역</h1>
          <p className="text-sm text-gray-500 mt-1">신청하신 요청의 처리 상태를 확인할 수 있습니다.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="my-rma-tabs">
          {TABS.map((t) => (
            <button key={t.k || 'all'} onClick={() => setTab(t.k)} data-testid={`my-rma-tab-${t.k || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${tab === t.k ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="my-rma-empty">
            <Package className="mx-auto text-gray-300" size={28} />
            <p className="mt-3 text-sm font-bold">요청 내역이 없습니다.</p>
            <Link to="/orders" className="mt-3 inline-block text-xs font-bold text-[#FF3B30] hover:underline">주문 내역으로 이동 →</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-rma-list">
            {rows.map((r) => {
              const M = TYPE_META[r.type] || TYPE_META.cancel;
              const Icon = M.icon;
              return (
                <button key={r.rma_id} onClick={() => openDetail(r.rma_id)} data-testid={`my-rma-row-${r.rma_id}`}
                  className="w-full text-left bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-4 hover:border-[#FF3B30]/40 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-black ${M.cls} dark:bg-white/5 dark:border-white/10 dark:text-white`}>
                        <Icon size={11} /> {M.label}
                      </span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full border border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300 font-bold">
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </div>
                    <ChevronRight size={14} className="text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500">주문 {r.order_number} · {fmtDate(r.created_at)}</p>
                  <p className="text-sm font-bold truncate mt-1">
                    {(r.items || []).slice(0, 1).map((it) => it.name_snapshot).join(', ') || '-'}
                    {(r.items || []).length > 1 && ` 외 ${r.items.length - 1}건`}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-gray-500">환불 예정</span>
                    <span className="font-black text-[#FF3B30]">{won(r.refund_calc?.total)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setDetail(null)} data-testid="my-rma-detail-drawer">
          <div className="w-full sm:max-w-md bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg font-black">요청 상세</p>
              <button onClick={() => setDetail(null)} className="text-xs text-gray-500">닫기 ✕</button>
            </div>
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3 text-xs space-y-1">
              <p><span className="text-gray-500">유형:</span> <span className="font-bold">{TYPE_META[detail.type]?.label || detail.type} / {STATUS_LABEL[detail.status] || detail.status}</span></p>
              <p><span className="text-gray-500">주문번호:</span> <span className="font-bold">{detail.order_number}</span></p>
              <p><span className="text-gray-500">판매자:</span> <span className="font-bold">{detail.seller_name_snapshot || '-'}</span></p>
              <p><span className="text-gray-500">신청 시각:</span> {fmtDate(detail.created_at)}</p>
              {detail.approved_at && <p><span className="text-gray-500">승인 시각:</span> {fmtDate(detail.approved_at)}</p>}
              {detail.completed_at && <p><span className="text-gray-500">완료 시각:</span> {fmtDate(detail.completed_at)}</p>}
              {detail.rejected_at && <p className="text-red-600 dark:text-red-400"><span className="text-gray-500">반려 시각:</span> {fmtDate(detail.rejected_at)}</p>}
            </div>
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3 text-xs space-y-1">
              <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">요청 상품</p>
              {(detail.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="truncate">{it.name_snapshot}</span>
                  <span className="font-bold">× {it.quantity} · {won(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3 text-xs space-y-1">
              <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">환불 계산 (Mock)</p>
              <div className="flex justify-between"><span className="text-gray-500">상품 금액</span><span>{won(detail.refund_calc?.product_total)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">배송비 환급</span><span>{won(detail.refund_calc?.refund_shipping)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">배송비 차감</span><span>− {won(detail.refund_calc?.shipping_deduct)}</span></div>
              <div className="flex justify-between pt-1 mt-1 border-t border-black/10 dark:border-white/10">
                <span className="font-bold">환불 예정 금액</span><span className="font-black text-[#FF3B30]" data-testid="my-rma-refund-total">{won(detail.refund_calc?.total)}</span>
              </div>
            </div>
            {detail.pickup?.tracking_number && (
              <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3 text-xs">
                <p className="font-bold mb-1">회수 배송</p>
                <p>{detail.pickup.courier} · <span className="font-mono">{detail.pickup.tracking_number}</span></p>
              </div>
            )}
            {detail.reshipment?.tracking_number && (
              <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3 text-xs">
                <p className="font-bold mb-1">재배송</p>
                <p>{detail.reshipment.courier} · <span className="font-mono">{detail.reshipment.tracking_number}</span></p>
              </div>
            )}
            {detail.reject_reason && (
              <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 mb-3 text-xs text-red-700 dark:text-red-300">
                <p className="font-black mb-1">반려 사유</p>
                <p className="whitespace-pre-wrap">{detail.reject_reason}</p>
              </div>
            )}
            {/* Timeline */}
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-3 text-xs">
              <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-2">처리 이력</p>
              <div className="space-y-1.5">
                {(detail.history || []).map((h) => (
                  <div key={h.history_id} className="flex items-start gap-2">
                    <CheckCircle2 size={11} className="text-emerald-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold">{STATUS_LABEL[h.action] || h.action}</p>
                      <p className="text-gray-500 text-[10px]">{fmtDate(h.at)}</p>
                      {h.note && <p className="text-gray-600 dark:text-gray-300">{h.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {detail.status === 'requested' && (
              <button onClick={() => withdraw(detail.rma_id)} className="w-full py-3 rounded-full border border-black/10 dark:border-white/10 text-xs font-black" data-testid="my-rma-withdraw-btn">
                요청 철회
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
